import type { components, CountdownApiClient } from "@countdown/api-client";
import { Plus } from "lucide-react";
import {
  Fragment,
  type ReactElement,
  type SyntheticEvent,
  useCallback,
  useState,
} from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { FileDrop, type Frame, MAX_SLIDES } from "@countdown/door-editor";

import { CropButton, type CropOutcome } from "@/components/CropButton";
import { FormField } from "@/components/FormField";
import { HelpTip } from "@/components/HelpTip";
import { LinkRow } from "@/components/LinkRow";
import { OutcomeNotice } from "@/components/Notice";
import { PageHeading } from "@/components/PageHeading";
import { SponsorHistory } from "@/components/SponsorHistory";
import { UnscannedAccept } from "@/components/UnscannedAccept";
import { SaveForm } from "@/components/SaveForm";
import { useSaveFirst, useSaveSection } from "@/components/SaveBar";
import { Advanced, Section } from "@/components/Section";
import { ColumnPicker } from "@/components/ColumnPicker";
import { StatusBadge, Tick } from "@/components/StatusBadge";
import { ViewTabs, type ViewTab } from "@/components/ViewTabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type ActionOutcome,
  type SponsorDetail,
  assetAddress,
  LOAD_FAILED,
  mediaSrc,
  revokeSponsorAccount,
  saveSponsor,
  sendAccessInvitation,
  setSponsorStatus,
  publishSponsorLogo,
  uploadSponsorLogo,
  useResource,
} from "@/lib/api";
import { useStoredColumns } from "@/lib/columns";
import { CUSTOMER_HELP_TIPS, HELP_TIPS, SPONSOR_RIGHT_TIPS } from "@/lib/help";
import {
  SPONSOR_RIGHT_LABELS,
  SPONSOR_STATUS_LABELS,
  type SponsorRight,
  actionChipLabel,
  can,
  formatDate,
} from "@/lib/labels";
import { CUSTOMER_WORDING, SPONSOR_WORDING, type Wording, firmPath } from "@/lib/wording";

type SponsorView = components["schemas"]["BackofficeSponsorView"];
type SponsorActionView = components["schemas"]["SponsorActionView"];

/**
 * "Gespeichert.", not "Erledigt.": saving sends no mail, and the report of
 * 23.09.2026 was a sponsor entered on production, waiting for one.
 */
const SAVED_MESSAGE = "Gespeichert.";

const LOGO_SAVED_MESSAGE = "Logo gespeichert.";
const LOGO_CUT_MESSAGE = "Logo zugeschnitten.";

const REVOKED_MESSAGE = "Verbindung gelöst. Die Person kann sich nicht mehr anmelden.";

type SponsorAccount = components["schemas"]["SponsorAccountView"];

type SponsorBody = Parameters<typeof saveSponsor>[0];
type RightsOverride = components["schemas"]["SponsorRightsOverride"];

type OverrideKey = keyof RightsOverride;
type OverrideValue = boolean | number | null | undefined;

/**
 * The settings of „Pflege und Portal" in order (H6): the two switches, then what an editing
 * sponsor may change (E6), the window's layout (D37) last. `whileEdits` names the answer to
 * „Sponsor pflegt selbst" under which a setting applies.
 */
const OVERRIDES: readonly {
  readonly key: OverrideKey;
  readonly label: string;
  readonly help?: string | undefined;
  /** The "?" under »Kunden« (E8), where it differs from `help`. */
  readonly customerHelp?: string | undefined;
  readonly whileEdits?: boolean;
}[] = [
  {
    key: "sponsor_edits",
    label: "Sponsor pflegt selbst",
    help: HELP_TIPS.sponsorEdits,
    customerHelp: CUSTOMER_HELP_TIPS.sponsorEdits,
  },
  {
    key: "pictures",
    label: "Bilder und Logo liefern",
    help: HELP_TIPS.sponsorPictures,
    customerHelp: CUSTOMER_HELP_TIPS.sponsorPictures,
    whileEdits: false,
  },
  { key: "max_slides", label: "Höchstzahl Folien", whileEdits: true },
  ...(Object.entries(SPONSOR_RIGHT_LABELS) as [SponsorRight, string][]).map(([key, label]) => ({
    key,
    label,
    help: SPONSOR_RIGHT_TIPS[key],
    customerHelp: key === "prize" ? CUSTOMER_HELP_TIPS.prizeUpgrade : undefined,
    whileEdits: true,
  })),
  { key: "layout", label: "Aufbau des Fensters ändern", whileEdits: true },
];

/** The box „Für diesen Sponsor abweichend" of a setting, by the setting's name. */
const DEVIATES = "_abweichend";

/**
 * The block „Pflege und Portal" as the upsert takes it: only the settings whose „Für diesen
 * Sponsor abweichend" is checked, so an unchecked one follows the action again.
 */
export function rightsOverrideOf(form: FormData): RightsOverride {
  const own: Record<string, boolean | number> = {};
  for (const { key } of OVERRIDES) {
    const raw = form.get(key);
    if (form.get(`${key}${DEVIATES}`) !== null && typeof raw === "string") {
      own[key] = key === "max_slides" ? Number(raw) : raw === "ja";
    }
  }
  return own as RightsOverride;
}

/** A value in the words of the form: a number, or ja and nein. */
function shown(value: boolean | number): string {
  return typeof value === "number" ? String(value) : value ? "ja" : "nein";
}

/**
 * One setting: what the action says, the box „Für diesen Sponsor abweichend", and only with it
 * the control. Checking the box starts from the other answer than the action's, since a
 * deviation equal to the action would change nothing.
 */
function OverrideRow({
  id,
  setting,
  own,
  inAction,
  actionCount,
  hidden,
  wording,
  onChange,
}: {
  readonly id: string;
  readonly setting: (typeof OVERRIDES)[number];
  readonly own: OverrideValue;
  readonly inAction: OverrideValue;
  readonly actionCount: number;
  readonly hidden: boolean;
  readonly wording: Wording;
  /** The sponsor's own answer as it is set now, `null` while it follows the action. */
  readonly onChange?: ((value: boolean | null) => void) | undefined;
}): ReactElement {
  const { key } = setting;
  const help =
    wording === CUSTOMER_WORDING ? (setting.customerHelp ?? setting.help) : setting.help;
  // The one setting named after the firm: »Kunde pflegt selbst« under »Kunden« (E8).
  const label = key === "sponsor_edits" ? `${wording.nominative} pflegt selbst` : setting.label;
  const [deviates, setDeviates] = useState(own !== null && own !== undefined);
  const control = `${id}-${key}`;
  const start = shown(
    own ?? (key === "max_slides" ? (inAction ?? MAX_SLIDES) : !(inAction ?? false)),
  );
  const options =
    key === "max_slides"
      ? Array.from({ length: MAX_SLIDES }, (_, index) => [String(index + 1), String(index + 1)])
      : [
          ["ja", "Ja"],
          ["nein", "Nein"],
        ];
  // The action's value describes both controls; the legend names the setting for both.
  const inActionLine = actionCount === 0 ? undefined : `${control}-aktion`;
  return (
    <fieldset hidden={hidden} aria-labelledby={`${control}-legend`}>
      <legend className="mb-1.5 flex items-center gap-2.5 text-sm leading-none font-medium">
        <span id={`${control}-legend`}>{label}</span>
        {help === undefined ? null : <HelpTip topic={label}>{help}</HelpTip>}
      </legend>
      <div className="grid content-start gap-1.5">
        {inActionLine === undefined ? null : (
          <p className="text-xs text-muted-foreground" id={inActionLine}>
            {inAction === null || inAction === undefined
              ? "In den Aktionen verschieden"
              : `${actionCount === 1 ? "In der Aktion" : "In den Aktionen"}: ${shown(inAction)}`}
          </p>
        )}
        <div className="flex items-center gap-2">
          <input
            className="border border-input"
            id={`${control}${DEVIATES}`}
            name={`${key}${DEVIATES}`}
            type="checkbox"
            aria-describedby={inActionLine}
            defaultChecked={deviates}
            onChange={(event) => {
              const checked = event.currentTarget.checked;
              setDeviates(checked);
              onChange?.(checked ? start === "ja" : null);
            }}
          />
          <label className="text-sm" htmlFor={`${control}${DEVIATES}`}>
            Für diesen {wording.oblique} abweichend
          </label>
        </div>
        {deviates ? (
          <select
            className="h-9 border border-input bg-background px-2 text-sm"
            id={control}
            name={key}
            aria-label={label}
            aria-describedby={inActionLine}
            defaultValue={start}
            onChange={(event) => {
              onChange?.(event.currentTarget.value === "ja");
            }}
          >
            {options.map(([option, text]) => (
              <option key={option} value={option}>
                {text}
              </option>
            ))}
          </select>
        ) : null}
      </div>
    </fieldset>
  );
}

/**
 * Where this sponsor deviates from what its actions set (H6, E6). On the company's page only:
 * a new sponsor follows the action. Which answer to „Sponsor pflegt selbst" holds decides which
 * settings apply; the others are hidden, not removed, so the form stays whole. Unknown (the
 * actions differ, or there is none) shows all.
 */
export function RightsOverrideFields({
  id,
  own,
  inAction,
  actionCount,
  wording = SPONSOR_WORDING,
}: {
  readonly id: string;
  readonly own: RightsOverride;
  readonly inAction: RightsOverride;
  readonly actionCount: number;
  readonly wording?: Wording;
}): ReactElement {
  const [ownEdits, setOwnEdits] = useState<boolean | null>(own.sponsor_edits ?? null);
  const edits = ownEdits ?? inAction.sponsor_edits ?? null;
  return (
    <fieldset className="mt-6">
      <legend className="text-base font-semibold">Pflege und Portal</legend>
      <div className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {OVERRIDES.map((setting) => (
          <OverrideRow
            key={setting.key}
            id={id}
            setting={setting}
            own={own[setting.key]}
            inAction={inAction[setting.key]}
            actionCount={actionCount}
            wording={wording}
            hidden={
              setting.whileEdits !== undefined && edits !== null && setting.whileEdits !== edits
            }
            onChange={setting.key === "sponsor_edits" ? setOwnEdits : undefined}
          />
        ))}
      </div>
    </fieldset>
  );
}

/**
 * The sponsors of this tenant (`/sponsoren`): the list, and "Sponsor anlegen" as a dialog.
 * Everything about one company is on its own page (`/sponsoren/:id`), so nothing opens above
 * or below the table any more (feedback of 05.10.2026).
 *
 * WHAT MOVED AWAY. Placing a sponsor on a door, the invitation and the release act on one door
 * of one action, so they are on the door page (Aktionen, Aktion, Türchen); what stays here
 * belongs to the company whatever it books.
 *
 * NO CONTACT DATA IN THE LIST. The table reports "hinterlegt / fehlt", because
 * that is what decides whether an invitation can go out at all. The address
 * itself is loaded for ONE company on its page: a screen that sits open all day
 * next to a coffee cup is not where addresses belong (concept 12.7).
 */
/**
 * The optional columns of the sponsor list. "Zustand" starts hidden: nothing distinguishes an
 * active sponsor from a prospect yet (16.10.2026), and the switch lives on the sponsor's page.
 */
const SPONSOR_COLUMNS = {
  industry: true,
  status: false,
  contact: true,
  invited: true,
  actions: true,
  booked: true,
  agreement: true,
} as const;

const SPONSOR_COLUMN_LABELS: Readonly<Record<keyof typeof SPONSOR_COLUMNS, string>> = {
  industry: "Branche",
  status: "Zustand",
  contact: "Ansprechpartner",
  invited: "Eingeladen",
  actions: "Aktionen",
  booked: "Belegte Türchen",
  agreement: "Übermittlungsvereinbarung",
};

/** One chip per running action; the full title on hover and for screen readers. */
function ActionChips({
  actions,
}: {
  readonly actions: readonly SponsorActionView[];
}): ReactElement | null {
  if (actions.length === 0) {
    return null;
  }
  return (
    <ul className="flex flex-wrap gap-1">
      {actions.map((action) => (
        <li
          key={action.action_id}
          title={action.title}
          className="border border-border px-1.5 py-0.5 text-xs font-medium whitespace-nowrap"
        >
          <span aria-hidden="true">{actionChipLabel(action)}</span>
          <span className="sr-only">{action.title}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The badge of the own sponsor's row, for the doors the publisher fills itself (H3): no
 * contact, invitation or agreement is owed, so the list asks for none. Every column says it,
 * since each can be the only one shown.
 */
const OWN_BADGE = <StatusBadge tone="neutral">Veranstalter</StatusBadge>;

/** A firm is a customer once it owns a calendar (E4). */
export function isCustomer(firm: SponsorView): boolean {
  return firm.customer_of.length > 0;
}

/**
 * A firm is a sponsor unless it is a customer only: a customer stands under »Sponsoren« as well
 * while it holds a door in a calendar not its own (E8).
 *
 * ponytail: `actions` leaves out finished and archived calendars (drafts are in it), so a
 * customer whose doors in other calendars all ended stands under »Kunden« only; a list of
 * every booked calendar would keep it under »Sponsoren« as well.
 */
export function isSponsor(firm: SponsorView): boolean {
  const own = new Set(firm.customer_of.map((action) => action.action_id));
  return own.size === 0 || (firm.actions ?? []).some((action) => !own.has(action.action_id));
}

/** The tab of the firm list (`?ansicht=kunden`); the firm's page keeps it in its address. */
export function firmView(search: URLSearchParams): Wording {
  return search.get("ansicht") === "kunden" ? CUSTOMER_WORDING : SPONSOR_WORDING;
}

/** »Sponsoren | Kunden« under the heading (E8). */
const FIRM_TABS: readonly ViewTab[] = [SPONSOR_WORDING, CUSTOMER_WORDING].map((tab) => ({
  label: tab.plural,
  to: `/sponsoren${tab.view}`,
}));

export function SponsorsPage({
  sponsors: firms,
  reloadOverview,
}: {
  readonly sponsors: readonly SponsorView[];
  /** Reload the shell's overview: the door pages pick their sponsors from it. */
  readonly reloadOverview: () => void;
}): ReactElement {
  const [columns, showColumn] = useStoredColumns("sponsoren", SPONSOR_COLUMNS);
  const [search] = useSearchParams();
  const wording = firmView(search);
  const customers = wording === CUSTOMER_WORDING;
  const sponsors = firms.filter(customers ? isCustomer : isSponsor);
  // Without leads there is nothing to agree to (D90); the column goes when nobody gets any (D94).
  const agreement = columns.agreement && sponsors.some((sponsor) => sponsor.leads === true);
  return (
    <>
      <PageHeading
        title={wording.plural}
        // A customer comes with its calendar (»Aktion anlegen«), not on its own.
        actions={customers ? undefined : <CreateSponsorDialog reloadOverview={reloadOverview} />}
      />
      <ViewTabs label="Firmen" tabs={FIRM_TABS} current={wording.plural} />

      <Section
        title={`Alle ${wording.plural}`}
        description={`${String(sponsors.length)} ${sponsors.length === 1 ? "Firma" : "Firmen"} angelegt`}
        actions={
          <ColumnPicker labels={SPONSOR_COLUMN_LABELS} columns={columns} onChange={showColumn} />
        }
      >
        <Table>
          <TableCaption className="sr-only">{wording.plural}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Firma</TableHead>
              {columns.industry ? <TableHead>Branche</TableHead> : null}
              {columns.status ? <TableHead>Zustand</TableHead> : null}
              {columns.contact ? <TableHead>Ansprechpartner</TableHead> : null}
              {columns.invited ? <TableHead>Eingeladen</TableHead> : null}
              {columns.actions ? <TableHead>Aktionen</TableHead> : null}
              {columns.booked ? (
                <TableHead className="text-center">Belegte Türchen</TableHead>
              ) : null}
              {agreement ? <TableHead>Übermittlungsvereinbarung</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sponsors.map((sponsor) => (
              <LinkRow key={sponsor.sponsor_id}>
                <TableCell className="min-w-48 font-medium">
                  {/* The company is the way to its page; a second "Bearbeiten" column said it twice. */}
                  <Link
                    className="underline underline-offset-4"
                    to={firmPath(sponsor.sponsor_id, wording)}
                  >
                    {sponsor.company_name}
                  </Link>
                  {sponsor.logo_pending === true ? (
                    <StatusBadge tone="warning" className="ml-2">
                      Logo prüfen
                    </StatusBadge>
                  ) : null}
                </TableCell>
                {columns.industry ? <TableCell>{sponsor.industry_category}</TableCell> : null}
                {columns.status ? (
                  <TableCell>
                    <StatusBadge tone={sponsor.status === "active" ? "success" : "neutral"}>
                      {SPONSOR_STATUS_LABELS[sponsor.status]}
                    </StatusBadge>
                  </TableCell>
                ) : null}
                {columns.contact ? (
                  <TableCell>
                    {sponsor.is_own ? (
                      OWN_BADGE
                    ) : (
                      <Tick
                        done={sponsor.contact_configured}
                        label={sponsor.contact_configured ? "Hinterlegt" : "Fehlt"}
                      />
                    )}
                  </TableCell>
                ) : null}
                {columns.invited ? (
                  <TableCell>
                    {sponsor.is_own ? (
                      OWN_BADGE
                    ) : (
                      <Tick
                        done={sponsor.invitation_sent === true}
                        label={sponsor.invitation_sent === true ? "Eingeladen" : "Offen"}
                      />
                    )}
                  </TableCell>
                ) : null}
                {columns.actions ? (
                  <TableCell>
                    {/* A customer's own calendars, in any status; a sponsor's running ones. */}
                    <ActionChips actions={customers ? sponsor.customer_of : (sponsor.actions ?? [])} />
                  </TableCell>
                ) : null}
                {columns.booked ? (
                  <TableCell className="text-center">{sponsor.booked_fields}</TableCell>
                ) : null}
                {agreement ? (
                  <TableCell>
                    {sponsor.is_own ? (
                      OWN_BADGE
                    ) : sponsor.leads !== true ? null : sponsor.transfer_agreement_accepted ? (
                      <StatusBadge tone="success">Angenommen</StatusBadge>
                    ) : (
                      <StatusBadge tone="warning">Offen</StatusBadge>
                    )}
                  </TableCell>
                ) : null}
              </LinkRow>
            ))}
            {sponsors.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    1 + Object.values(columns).filter(Boolean).length - (columns.agreement && !agreement ? 1 : 0)
                  }
                  className="py-6 text-center text-muted-foreground"
                >
                  Noch kein {wording.nominative}.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Section>
    </>
  );
}

/** "Sponsor anlegen": the company fields in a dialog, then straight to the company's page. */
function CreateSponsorDialog({
  reloadOverview,
}: {
  readonly reloadOverview: () => void;
}): ReactElement {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = (body: SponsorBody): void => {
    setBusy(true);
    setError(null);
    saveSponsor(body)
      .then((result) => {
        if (!result.ok) {
          setError(result.message);
          return;
        }
        reloadOverview();
        setOpen(false);
        void navigate(`/sponsoren/${result.detail.sponsor_id}`);
      })
      .catch((caught: unknown) => {
        console.error("[SponsorsPage] create failed", caught);
        setError(LOAD_FAILED);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setError(null);
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus aria-hidden="true" />
          Sponsor anlegen
        </Button>
      </DialogTrigger>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Sponsor anlegen</DialogTitle>
          <DialogDescription>Logo und Zugang folgen auf der Seite der Firma.</DialogDescription>
        </DialogHeader>
        <SponsorForm id="sponsor-neu" detail={null} onSave={handleSave} />
        {error !== null ? <OutcomeNotice outcome={{ ok: false, message: error }} /> : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
          >
            Abbrechen
          </Button>
          <Button type="submit" form="sponsor-neu" disabled={busy}>
            {busy ? "Wird angelegt …" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The one place in this application that writes a contact address.
 *
 * Uncontrolled inputs with `defaultValue`, keyed on the sponsor: the form is
 * either empty (new) or pre-filled once, and React state per field would buy
 * nothing but re-renders.
 *
 * An empty field is sent as `null`, which the API reads as "leave it alone".
 * That is the safer direction: correcting an industry category must not drop
 * the address the invitation needs. With a sponsor the id goes along, so a
 * corrected company name renames it instead of adding a second sponsor.
 *
 * `services` (D90) is sent whole on the company's page, except for the own sponsor, which has
 * no portal. A checkbox has no empty state to mean "unchanged": unchecked is an answer. Its
 * predecessor `live_dashboard` was missing here entirely until 18.09.2026, so every sponsor on
 * package "Mittel" entered on this screen went live without the dashboard they had booked.
 * »Sponsor anlegen« sends it only with a tick: the name is the key, and a name that exists
 * already would otherwise switch that firm's leads off.
 *
 * On the company's page „Pflege und Portal" is always sent whole for the same reason: an
 * unchecked „Für diesen Sponsor abweichend" is an answer too and takes back an earlier
 * deviation. Not for the own sponsor (H3), which follows the action in everything, and not
 * without its element (D57): then the block does not appear and the server keeps what it has.
 */
export function sponsorBodyOf(
  form: FormData,
  detail: SponsorDetail | null,
  rights: boolean,
): SponsorBody | null {
  const value = (name: string): string | null => {
    const raw = form.get(name);
    return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : null;
  };
  const company = value("company_name");
  if (company === null) {
    return null;
  }
  const services = {
    leads: form.get("leads") !== null,
    leads_list: form.get("leads_list") !== null,
    leads_csv: form.get("leads_csv") !== null,
    stats: form.get("stats") !== null,
  };
  const sendServices =
    detail === null ? Object.values(services).some(Boolean) : !detail.is_own;
  return {
    ...(detail === null ? {} : { sponsor_id: detail.sponsor_id }),
    company_name: company,
    contact_name: value("contact_name"),
    contact_email: value("contact_email"),
    industry_category: value("industry_category"),
    ...(sendServices ? { services } : {}),
    ...(detail === null || detail.is_own || !rights
      ? {}
      : { sponsor_rights: rightsOverrideOf(form) }),
  };
}

/**
 * The fields of a company. `onInvite` puts »Einladen« beside the address (22.10.2026): the
 * company's page offers it while the saved address has had no invitation yet.
 */
function SponsorFields({
  id,
  detail,
  rights,
  onInvite,
  busy = false,
  wording = SPONSOR_WORDING,
}: {
  readonly id: string;
  readonly detail: SponsorDetail | null;
  readonly rights: boolean;
  readonly onInvite?: (() => void) | undefined;
  /** A step of the page is out: no second invitation meanwhile. */
  readonly busy?: boolean;
  readonly wording?: Wording;
}): ReactElement {
  return (
    <>
      <div className="grid gap-x-6 sm:grid-cols-2">
        <FormField label="Firma" name="company_name" defaultValue={detail?.company_name} required />
        <FormField
          label="Branche"
          name="industry_category"
          defaultValue={detail?.industry_category}
        />
        <FormField
          label="Ansprechpartner"
          name="contact_name"
          defaultValue={detail?.contact_name}
        />
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <FormField
              label="E-Mail für die Einladung"
              name="contact_email"
              type="email"
              defaultValue={detail?.contact_email}
            />
          </div>
          {onInvite === undefined ? null : (
            <Button type="button" variant="outline" disabled={busy} onClick={onInvite}>
              Einladen
            </Button>
          )}
        </div>
      </div>
      {detail?.is_own === true ? null : (
        <ServiceFields id={id} services={detail?.services ?? null} wording={wording} />
      )}
      {detail === null ? null : detail.is_own ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Veranstalter: Ihre eigenen Türchen. Kein Portal, keine Freigabe durch einen
          Sponsor.
        </p>
      ) : !rights ? null : (
        <RightsOverrideFields
          id={id}
          own={detail.sponsor_rights}
          inAction={detail.action_rights}
          actionCount={detail.action_count}
          wording={wording}
        />
      )}
    </>
  );
}

/**
 * What the publisher grants the firm (D90), each off until ticked. The two ways to the leads
 * appear only with the leads (D53): without them there is nothing to list or export, and an
 * unticked »Leads« sends both as off.
 */
function ServiceFields({
  id,
  services,
  wording,
}: {
  readonly id: string;
  readonly services: SponsorDetail["services"] | null;
  readonly wording: Wording;
}): ReactElement {
  const [leads, setLeads] = useState(services?.leads ?? false);
  const tips = wording === CUSTOMER_WORDING ? CUSTOMER_HELP_TIPS : HELP_TIPS;
  return (
    <div className="mt-5 grid gap-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${id}-leads`}
          name="leads"
          checked={leads}
          onCheckedChange={(checked) => {
            setLeads(checked === true);
          }}
        />
        <Label htmlFor={`${id}-leads`}>Leads</Label>
        <HelpTip topic="Leads">{tips.leads}</HelpTip>
      </div>
      {leads ? (
        <div className="ml-6 flex flex-wrap gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${id}-leads-list`}
              name="leads_list"
              defaultChecked={services?.leads_list ?? false}
            />
            <Label htmlFor={`${id}-leads-list`}>Liste im {wording.portal}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${id}-leads-csv`}
              name="leads_csv"
              defaultChecked={services?.leads_csv ?? false}
            />
            <Label htmlFor={`${id}-leads-csv`}>CSV-Export</Label>
          </div>
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <Checkbox id={`${id}-stats`} name="stats" defaultChecked={services?.stats ?? false} />
        <Label htmlFor={`${id}-stats`}>Auswertung</Label>
        <HelpTip topic="Auswertung">{tips.stats}</HelpTip>
      </div>
    </div>
  );
}

/** The company's fields as a plain form, for "Sponsor anlegen", whose dialog has its button. */
export function SponsorForm({
  id,
  detail,
  rights = false,
  onSave,
}: {
  /** The form's id, so the dialog's button outside it can submit it. */
  readonly id: string;
  readonly detail: SponsorDetail | null;
  /** Shows and sends „Pflege und Portal" (the element of a sponsor's own rights). */
  readonly rights?: boolean;
  readonly onSave: (body: SponsorBody) => void;
}): ReactElement {
  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const body = sponsorBodyOf(new FormData(event.currentTarget), detail, rights);
    if (body !== null) {
      onSave(body);
    }
  };

  return (
    <form id={id} key={detail?.sponsor_id ?? "neu"} onSubmit={handleSubmit}>
      <SponsorFields id={id} detail={detail} rights={rights} />
    </form>
  );
}

/**
 * One company (`/sponsoren/:sponsorId`): its data, its doors, its logo, and its access.
 * One card each; data and logo save with the page's bar, the switch that shuts the access
 * stands last and in red.
 */
export function SponsorDetailPage({
  sponsors,
  reloadOverview,
  granted,
}: {
  /** The overview's list: it knows whether the company has had an invitation. */
  readonly sponsors: readonly SponsorView[];
  readonly reloadOverview: () => void;
  readonly granted: readonly string[];
}): ReactElement {
  const { sponsorId = "" } = useParams();
  const load = useCallback(
    (api: CountdownApiClient) =>
      api.GET("/api/v1/backoffice/sponsors/{sponsor_id}", {
        params: { path: { sponsor_id: sponsorId } },
      }),
    [sponsorId],
  );
  const sponsor = useResource<SponsorDetail>(load);
  // The tab it was opened from (E8): a customer's page speaks of »Kunde«.
  const [search] = useSearchParams();
  const wording = firmView(search);
  const back = { to: `/sponsoren${wording.view}`, label: `Alle ${wording.plural}` };

  if (sponsor.status !== "ready") {
    return (
      <>
        <PageHeading title={wording.nominative} back={back} />
        <p className="text-sm text-muted-foreground">
          {sponsor.status === "loading"
            ? "Wird geladen …"
            : sponsor.status === "not_found"
              ? `Diesen ${wording.oblique} gibt es nicht.`
              : LOAD_FAILED}
        </p>
      </>
    );
  }

  // »Übersicht« first (D93), »Stammdaten« as `?reiter=stammdaten`, beside the list's tab.
  const masterData = search.get("reiter") === "stammdaten";
  const tabLink = (toMasterData: boolean): string => {
    const next = new URLSearchParams(search);
    if (toMasterData) {
      next.set("reiter", "stammdaten");
    } else {
      next.delete("reiter");
    }
    const query = next.toString();
    return `/sponsoren/${sponsorId}${query === "" ? "" : `?${query}`}`;
  };
  const tabs = (
    <ViewTabs
      label="Ansicht"
      tabs={[
        { label: "Übersicht", to: tabLink(false) },
        { label: "Stammdaten", to: tabLink(true) },
      ]}
      current={masterData ? "Stammdaten" : "Übersicht"}
    />
  );

  if (!masterData) {
    return (
      <>
        <PageHeading title={sponsor.data.company_name} back={back} />
        {tabs}
        <SponsorHistory
          detail={sponsor.data}
          granted={granted}
          reloadDetail={sponsor.reload}
          masterDataLink={tabLink(true)}
          wording={wording}
        />
      </>
    );
  }

  return (
    <LoadedSponsor
      key={sponsor.data.sponsor_id}
      tabs={tabs}
      detail={sponsor.data}
      reload={sponsor.reload}
      reloadOverview={reloadOverview}
      invited={
        sponsors.find((entry) => entry.sponsor_id === sponsor.data.sponsor_id)?.invitation_sent ===
        true
      }
      back={back}
      wording={wording}
      mayAcceptUnscanned={can(granted, "approve_unscanned")}
      mayManageAccess={can(granted, "sponsor_access")}
      mayEditRights={can(granted, "sponsor_rights")}
    />
  );
}

function LoadedSponsor({
  tabs,
  detail,
  reload,
  reloadOverview,
  invited,
  back,
  wording,
  mayAcceptUnscanned,
  mayManageAccess,
  mayEditRights,
}: {
  readonly tabs: ReactElement;
  readonly detail: SponsorDetail;
  readonly reload: () => void;
  readonly reloadOverview: () => void;
  /** An invitation has gone out to the company. */
  readonly invited: boolean;
  readonly back: { readonly to: string; readonly label: string };
  readonly wording: Wording;
  readonly mayAcceptUnscanned: boolean;
  /** Revoke and invite: who sees a company's transmitted contacts. */
  readonly mayManageAccess: boolean;
  /** „Pflege und Portal" of this company (D57). */
  readonly mayEditRights: boolean;
}): ReactElement {
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  // The last logo could not be checked: the tenant admin may take it with a reason.
  const [unscanned, setUnscanned] = useState(false);
  // »Einladen« queued an invitation here. `invited` turns only once the mail reached the
  // server, which on a host without one is never: the button goes now, so a second click
  // does not queue a second invitation to the same address.
  const [invitedNow, setInvitedNow] = useState(false);

  const run = (action: () => Promise<ActionOutcome>): void => {
    setBusy(true);
    setOutcome(null);
    action()
      .then((result) => {
        setOutcome(result);
        if (result.ok) {
          reload();
          reloadOverview();
        }
      })
      .catch((error: unknown) => {
        console.error("[SponsorsPage] write failed", error);
        setOutcome({ ok: false, message: "Der Schritt konnte nicht ausgeführt werden." });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const inactive = detail.status === "inactive";
  const saveFirst = useSaveFirst();

  // The invitation carries the portal of one calendar (D36): one the sponsor holds doors in and
  // whose portal it opens (`invitable`, the server's rule): a running one, or the customer's own
  // calendar in draft (E6).
  const invitable = detail.booked_doors.find((doors) => doors.invitable);
  const invite = (email?: string, done?: () => void): void => {
    run(async () => {
      const result = await sendAccessInvitation(detail.sponsor_id, email, invitable?.action_id);
      if (result.ok) {
        done?.();
      }
      return result;
    });
  };

  // »Einladen« at the contact (22.10.2026): it sends the saved address, so the bar saves
  // first and the invitation goes only when everything got saved. Only where the server takes
  // it: elsewhere it refuses before the go-live (D53, nothing unusable).
  const offerInvite =
    invitable !== undefined &&
    mayManageAccess &&
    !detail.is_own &&
    !inactive &&
    !invited &&
    !invitedNow &&
    (detail.contact_email ?? "") !== "";

  return (
    <>
      <PageHeading title={detail.company_name} back={back} />
      {tabs}
      {outcome !== null ? <OutcomeNotice outcome={outcome} className="mb-6" /> : null}

      <Section title="Firma und Kontakt">
        <SaveForm
          label="Firma und Kontakt"
          readOnly={false}
          submit={async (form) => {
            const body = sponsorBodyOf(form, detail, mayEditRights);
            if (body === null) {
              return { ok: false, message: "Bitte die Firma angeben." };
            }
            const result = await saveSponsor(body);
            return result.ok ? { ok: true, message: SAVED_MESSAGE } : result;
          }}
          onSaved={() => {
            reload();
            reloadOverview();
          }}
        >
          <SponsorFields
            id="sponsor-daten"
            detail={detail}
            rights={mayEditRights}
            busy={busy}
            wording={wording}
            onInvite={
              offerInvite
                ? () => {
                    void saveFirst().then((saved) => {
                      if (saved) {
                        invite(undefined, () => {
                          setInvitedNow(true);
                        });
                      }
                    });
                  }
                : undefined
            }
          />
        </SaveForm>
      </Section>

      <Section title="Belegte Türchen">
        {detail.booked_doors.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch kein Türchen belegt.</p>
        ) : (
          <ul className="grid gap-2 text-sm">
            {detail.booked_doors.map((doors) => (
              <li key={doors.action_id}>
                <span className="text-muted-foreground">{doors.action_title}: </span>
                {doors.positions.map((position, index) => (
                  <Fragment key={position}>
                    {index > 0 ? ", " : null}
                    <Link
                      className="font-medium underline underline-offset-4"
                      to={`/aktionen/${doors.action_id}/tuerchen/${String(position)}`}
                    >
                      Türchen {position}
                    </Link>
                  </Fragment>
                ))}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <SponsorLogo
        detail={detail}
        busy={busy}
        wording={wording}
        acceptUnscanned={mayAcceptUnscanned && unscanned}
        onUpload={async (file, altText, reason) => {
          const result = await uploadSponsorLogo(detail.sponsor_id, {
            file,
            altText,
            unscannedReason: reason,
          });
          setUnscanned(!result.ok && result.unscanned === true);
          if (!result.ok) {
            return { ok: false, message: result.message };
          }
          reload();
          reloadOverview();
          return { ok: true, message: LOGO_SAVED_MESSAGE };
        }}
        onCrop={async (assetId, crop) => {
          const result = await uploadSponsorLogo(detail.sponsor_id, { assetId, crop });
          if (result.ok) {
            run(() => Promise.resolve({ ok: true, message: LOGO_CUT_MESSAGE }));
          }
          return result;
        }}
        onCheck={async (assetId, frame) => {
          // The whole picture is released as sent; a frame goes up as a new cut, public at once.
          const whole = frame.x === 0 && frame.y === 0 && frame.w === 1 && frame.h === 1;
          const result = whole
            ? await publishSponsorLogo(detail.sponsor_id)
            : await uploadSponsorLogo(detail.sponsor_id, { assetId, crop: frame });
          if (result.ok) {
            run(() => Promise.resolve({ ok: true, message: LOGO_RELEASED_MESSAGE }));
            reloadOverview();
            return { ok: true };
          }
          return { ok: false, message: result.message };
        }}
      />

      {/* The own sponsor has no portal (H3): nothing to invite, nothing to shut. */}
      {detail.is_own ? null : (
        <Section
          title={wording.access}
          description={
            inactive
              ? "Der Zugang ist stillgelegt."
              : "Die Firma meldet sich mit persönlichen Konten an. Stilllegen sperrt alle sofort, Freigeben geht jederzeit wieder."
          }
        >
          <SponsorAccounts
            accounts={detail.accounts ?? []}
            busy={busy}
            wording={wording}
            mayManage={mayManageAccess}
            onRevoke={(account) => {
              run(async () => {
                const result = await revokeSponsorAccount(
                  detail.sponsor_id,
                  account.sponsor_account_id,
                );
                return result.ok ? { ok: true, message: REVOKED_MESSAGE } : result;
              });
            }}
          />
          {mayManageAccess && !inactive ? (
            <AccessInvitation busy={busy} onInvite={invite} />
          ) : null}
          {/* The access is daily work and stays neutral; only shutting it is set apart at the end. */}
          <h3
            className={
              inactive
                ? "mt-8 mb-3 text-base font-bold"
                : "mt-10 mb-3 border-t border-destructive/30 pt-6 text-base font-bold text-destructive"
            }
          >
            {inactive ? "Zugang freigeben" : "Zugang stilllegen"}
          </h3>
          {inactive ? (
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => {
                run(() => setSponsorStatus(detail.sponsor_id, "active"));
              }}
            >
              Zugang wieder freigeben
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={busy}>
                  Zugang stilllegen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Zugang von {detail.company_name} stilllegen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Die Firma kann sich ab sofort nicht mehr im {wording.portal} anmelden.
                    Belegte Türchen bleiben, wie sie sind.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Zurück</AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    onClick={() => {
                      run(() => setSponsorStatus(detail.sponsor_id, "inactive"));
                    }}
                  >
                    Stilllegen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </Section>
      )}
    </>
  );
}

/**
 * The accounts that may sign in for this company (D34): one row per person, newest first.
 *
 * The address is the one verified when the person set up the access. "Verbindung lösen"
 * ends that one person's access at their next click; the company's other accounts stay.
 */
function SponsorAccounts({
  accounts,
  busy,
  wording,
  mayManage,
  onRevoke,
}: {
  readonly accounts: readonly SponsorAccount[];
  readonly busy: boolean;
  readonly wording: Wording;
  readonly mayManage: boolean;
  readonly onRevoke: (account: SponsorAccount) => void;
}): ReactElement {
  return (
    <>
      <h3 className="mb-3 text-base font-bold">Verbundene Konten</h3>
      {accounts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Noch kein Konto verbunden. Die Kontaktperson verbindet ihr Konto beim ersten Öffnen der
          Einladung.
        </p>
      ) : (
        <ul className="grid gap-3">
          {accounts.map((account) => {
            const address = account.email ?? "ohne E-Mail";
            const signedInSince =
              account.last_sign_in_at !== null &&
              account.last_sign_in_at !== undefined &&
              account.last_sign_in_at !== account.bound_at;
            return (
              <li
                key={account.sponsor_account_id}
                className="flex flex-col gap-2 border-b border-border pb-3 text-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-medium">{address}</p>
                  <p className="text-muted-foreground">
                    verbunden am {formatDate(account.bound_at)},{" "}
                    {signedInSince && account.last_sign_in_at
                      ? `zuletzt angemeldet am ${formatDate(account.last_sign_in_at)}`
                      : "seit der Einrichtung nicht angemeldet"}
                  </p>
                </div>
                {mayManage ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" disabled={busy}>
                        Verbindung lösen
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Verbindung von {address} lösen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Diese Person kann sich ab sofort nicht mehr im {wording.portal}{" "}
                          anmelden. Für einen neuen Zugang braucht sie eine neue Einladung.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Zurück</AlertDialogCancel>
                        <AlertDialogAction
                          variant="destructive"
                          onClick={() => {
                            onRevoke(account);
                          }}
                        >
                          Lösen
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

/**
 * "Weitere Person einladen" (D34): an invitation binds once and only for the address it was
 * sent to, so a colleague of the contact gets their own. Empty field: the contact address.
 * Folded away (22.10.2026): the contact is invited with »Einladen« beside the address.
 */
export function AccessInvitation({
  busy,
  onInvite,
}: {
  readonly busy: boolean;
  /** `done` clears the field once the invitation is queued. */
  readonly onInvite: (email: string, done: () => void) => void;
}): ReactElement {
  const [email, setEmail] = useState("");

  return (
    <Advanced summary="Weitere Person einladen" className="mt-8">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onInvite(email, () => {
            setEmail("");
          });
        }}
      >
        <div className="grid gap-1.5 sm:max-w-md">
          <Label htmlFor="zugang-einladen">E-Mail-Adresse</Label>
          <Input
            id="zugang-einladen"
            className="bg-background"
            type="email"
            value={email}
            maxLength={320}
            disabled={busy}
            aria-describedby="zugang-einladen-hinweis"
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <p id="zugang-einladen-hinweis" className="text-sm text-muted-foreground">
            Leer lassen, um die Kontakt-E-Mail einzuladen.
          </p>
        </div>
        <Button className="mt-3" type="submit" variant="outline" disabled={busy}>
          Einladung senden
        </Button>
      </form>
    </Advanced>
  );
}

const LOGO_RELEASED_MESSAGE = "Logo freigegeben. Es steht jetzt im Kalender.";

/**
 * The logo on this sponsor's calendar doors: what is there, and a way to replace it.
 *
 * Without any action gate: a logo belongs to the company, not to a door, and entering it
 * before the campaign starts is exactly when it is wanted.
 *
 * A logo the sponsor sent waits for the check (D94): the section stands out, shows it with
 * »Noch nicht freigegeben«, and offers one step, »Logo prüfen«: the cut, whose »Übernehmen«
 * releases it. Uploading another one folds away until then.
 */
function SponsorLogo({
  detail,
  busy,
  wording,
  acceptUnscanned,
  onUpload,
  onCrop,
  onCheck,
}: {
  readonly detail: SponsorDetail;
  readonly busy: boolean;
  readonly wording: Wording;
  /** Offer "Ungeprüft übernehmen" for the file just refused (tenant admin only). */
  readonly acceptUnscanned: boolean;
  readonly onUpload: (
    file: File,
    altText: string,
    unscannedReason?: string,
  ) => Promise<ActionOutcome>;
  /** A new cut of the logo, released or waiting (D44); public at once, like every upload here. */
  readonly onCrop: (assetId: string, crop: Frame) => Promise<CropOutcome>;
  /** Check the logo the sponsor uploaded: release it whole (D40) or as a cut (D44). */
  readonly onCheck: (assetId: string, frame: Frame) => Promise<CropOutcome>;
}): ReactElement {
  const [file, setFile] = useState<File | null>(null);
  const [altText, setAltText] = useState("");
  const [notice, setNotice] = useState<ActionOutcome | null>(null);
  // A new drop zone after an upload: a file input cannot be emptied through its value.
  const [drop, setDrop] = useState(0);
  const logo = detail.logo ?? null;
  const waiting =
    detail.logo_pending === true && detail.logo_asset_id !== null && detail.logo_asset_id !== undefined
      ? detail.logo_asset_id
      : null;

  const send = async (reason?: string): Promise<ActionOutcome> => {
    if (file === null) {
      return { ok: true };
    }
    setNotice(null);
    const outcome = await onUpload(file, altText, reason);
    if (outcome.ok) {
      setFile(null);
      setAltText("");
      setDrop((count) => count + 1);
    }
    return outcome;
  };

  // A chosen file goes up with the page's »Speichern« (22.10.2026), like a slot of the door's
  // configurator; a refusal stays here, success is the bar's to say.
  const inBar = useSaveSection({
    label: "Logo",
    dirty: file !== null,
    save: async () => {
      const outcome = await send();
      setNotice(outcome.ok ? null : outcome);
      return outcome.ok ? "ok" : "failed";
    },
  });

  const upload = (
    <div className="grid gap-4">
      <FileDrop
        key={drop}
        id="logodatei"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        prompt="Logo auswählen oder hierher ziehen"
        hint="PNG, JPEG, WebP oder SVG"
        disabled={busy}
        onChange={setFile}
      />
      <div className="grid gap-1.5">
        <Label htmlFor="logo-beschreibung">Beschreibung</Label>
        <Input
          id="logo-beschreibung"
          value={altText}
          maxLength={200}
          disabled={busy}
          onChange={(event) => {
            setAltText(event.target.value);
          }}
        />
      </div>
    </div>
  );

  return (
    <Section
      id="logo"
      title="Logo im Kalender"
      className={waiting === null ? undefined : "border-l-4 border-notice pl-4"}
      footer={
        inBar ? undefined : (
          <Button
            variant="outline"
            disabled={busy || file === null}
            onClick={() => {
              void send().then(setNotice);
            }}
          >
            Logo hochladen
          </Button>
        )
      }
    >
      <div className="grid gap-6 sm:grid-cols-[12rem_1fr]">
        <div className="grid content-start gap-2">
          <div className="flex h-28 items-center justify-center border border-border bg-surface-sunken p-3">
            {waiting !== null ? (
              <img
                className="max-h-full w-auto max-w-full object-contain"
                src={assetAddress(waiting)}
                alt={`Logo von ${detail.company_name}`}
              />
            ) : logo === null ? (
              <p className="text-center text-sm text-muted-foreground">Noch kein Logo.</p>
            ) : (
              <img
                className="max-h-full w-auto max-w-full object-contain"
                src={mediaSrc(logo.path)}
                alt={logo.alt_text}
              />
            )}
          </div>
          {waiting !== null ? (
            <p className="text-sm text-muted-foreground">
              <StatusBadge tone="warning">Noch nicht freigegeben</StatusBadge>{" "}
              Vom {wording.oblique} hochgeladen.
            </p>
          ) : null}
        </div>
        <div className="grid content-start gap-4">
          {waiting !== null ? (
            <CropButton
              assetId={waiting}
              alt={`Logo von ${detail.company_name}`}
              label="Logo prüfen und freigeben"
              text="Logo prüfen"
              primary
              disabled={busy}
              onApply={(frame) => onCheck(waiting, frame)}
            />
          ) : detail.logo_asset_id === null || detail.logo_asset_id === undefined ? null : (
            <CropButton
              assetId={detail.logo_asset_id}
              alt={logo?.alt_text ?? detail.company_name}
              label="Logo zuschneiden"
              disabled={busy}
              onApply={(crop) => onCrop(detail.logo_asset_id ?? "", crop)}
            />
          )}
          {waiting !== null ? (
            <Advanced summary="Anderes Logo hochladen">{upload}</Advanced>
          ) : (
            upload
          )}
          {acceptUnscanned && file !== null ? (
            <UnscannedAccept
              busy={busy}
              onAccept={(reason) => {
                void send(reason).then(setNotice);
              }}
            />
          ) : null}
          {notice === null ? null : <OutcomeNotice outcome={notice} />}
        </div>
      </div>
    </Section>
  );
}
